import type { Dispatch, SetStateAction } from "react";
import {
  X,
  Camera,
  Image as ImageIcon,
  Package,
  Plus,
  Clock,
  AlertTriangle,
  Sparkles,
  Mic,
  MicOff,
  Loader2,
  Users,
  Truck,
  User,
} from "lucide-react";
import type { Chiamata } from "../../shared/types";
import { toggleMultiOpzione, isMultiOpzioneSelected } from "../../shared/constants";

export type MaterialeRow = {
  descrizione: string;
  quantita: string;
  um?: "N." | "MT.";
};

export interface ReportWizardStepsProps {
  step: 1 | 2 | 3 | 4 | 5;
  c: Chiamata;
  statoImpiantoArrivo: string;
  setStatoImpiantoArrivo: (val: string) => void;
  statoImpiantoAltro: string;
  setStatoImpiantoAltro: (val: string) => void;
  statiImpiantoOpzioni: string[];
  causaGuasto: string;
  setCausaGuasto: (val: string) => void;
  causeGuastoOpzioni: string[];
  inizioLavoroAt: string;
  setInizioLavoroAt: (val: string) => void;
  fineLavoroAt: string;
  setFineLavoroAt: (val: string) => void;
  errOrari: { tipo: string; titolo: string; messaggio: string } | null;
  isAiEnabled: boolean;
  isListening: boolean;
  handleToggleSpeech: () => void;
  isAiLoading: boolean;
  handleMiglioraConAi: () => void;
  azioniEseguite: string;
  setAzioniEseguite: (val: string) => void;
  materiali: MaterialeRow[];
  setMateriali: Dispatch<SetStateAction<MaterialeRow[]>>;
  aggiornaMateriale: (index: number, field: keyof MaterialeRow, value: string) => void;
  rimuoviMateriale: (index: number) => void;
  esito: string;
  setEsito: (val: string) => void;
  esitiOpzioni: string[];
  fotoFiles: File[];
  rimuoviFoto: (index: number) => void;
  aggiungiFoto: (files: File[]) => void;
  compressioneFoto: boolean;
  statoDestinazione: "chiusa" | "sospesa";
  setStatoDestinazione: (val: "chiusa" | "sospesa") => void;
  nomeSquadra?: string;
  collaboratori?: string;
  mezzoTarga?: string;
  autista?: string;
  onModificaCollaboratori?: () => void;
}

export function ReportWizardSteps({
  step,
  c,
  statoImpiantoArrivo,
  setStatoImpiantoArrivo,
  statoImpiantoAltro,
  setStatoImpiantoAltro,
  statiImpiantoOpzioni,
  causaGuasto,
  setCausaGuasto,
  causeGuastoOpzioni,
  inizioLavoroAt,
  setInizioLavoroAt,
  fineLavoroAt,
  setFineLavoroAt,
  errOrari,
  isAiEnabled,
  isListening,
  handleToggleSpeech,
  isAiLoading,
  handleMiglioraConAi,
  azioniEseguite,
  setAzioniEseguite,
  materiali,
  setMateriali,
  aggiornaMateriale,
  rimuoviMateriale,
  esito,
  setEsito,
  esitiOpzioni,
  fotoFiles,
  rimuoviFoto,
  aggiungiFoto,
  compressioneFoto,
  statoDestinazione,
  setStatoDestinazione,
  nomeSquadra,
  collaboratori,
  mezzoTarga,
  autista,
  onModificaCollaboratori,
}: ReportWizardStepsProps) {
  return (
      <div className="space-y-4">
        {/* PASSO 1: ARRIVO E DIAGNOSI */}
        {step === 1 && (
          <section className="card-section space-y-4 animate-fade-in p-5">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
              <div className="w-7 h-7 rounded-full bg-slate-900 text-white font-extrabold text-xs flex items-center justify-center">
                1
              </div>
              <div>
                <h3 className="section-title mb-0">Arrivo e diagnosi guasto</h3>
                <p className="text-xs text-slate-500">Stato riscontrato e causa presunta all'arrivo della squadra</p>
              </div>
            </div>

            {/* Box Composizione Squadra e Collaboratori del Turno */}
            <div className="bg-gradient-to-r from-blue-50/80 to-slate-50 border border-blue-200/80 rounded-2xl p-3.5 space-y-2 shadow-2xs">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-xs">
                    <Users className="w-4 h-4 text-blue-100" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-black uppercase tracking-wider text-blue-700">
                      Dotazione e Squadra del Turno
                    </div>
                    <div className="text-xs font-bold text-slate-800 truncate">
                      {nomeSquadra || 'La tua squadra'}
                      {collaboratori?.trim() ? (
                        <span className="text-blue-800 font-extrabold"> + {collaboratori.trim()}</span>
                      ) : (
                        <span className="text-slate-500 font-medium"> (Nessun collaboratore / Solo)</span>
                      )}
                    </div>
                  </div>
                </div>
                {onModificaCollaboratori && (
                  <button
                    type="button"
                    onClick={onModificaCollaboratori}
                    className="px-2.5 py-1 text-[11px] font-bold text-blue-700 bg-white hover:bg-blue-50 border border-blue-200 rounded-lg shrink-0 shadow-2xs transition active:scale-95 cursor-pointer"
                  >
                    Modifica
                  </button>
                )}
              </div>

              {(mezzoTarga || autista) && (
                <div className="flex flex-wrap items-center gap-2 text-[11px] pt-1.5 border-t border-blue-100 text-slate-700">
                  {mezzoTarga && (
                    <span className="inline-flex items-center gap-1 font-semibold bg-white px-2 py-0.5 rounded-md border border-slate-200">
                      <Truck className="w-3 h-3 text-blue-600" />
                      <span>Mezzo: <strong>{mezzoTarga}</strong></span>
                    </span>
                  )}
                  {autista && (
                    <span className="inline-flex items-center gap-1 font-semibold bg-white px-2 py-0.5 rounded-md border border-slate-200">
                      <User className="w-3 h-3 text-amber-600" />
                      <span>Guida: <strong>{autista}</strong></span>
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-3.5">
              {c.impianto?.tipo === 'IS' ? (
                <div>
                  <label className="label">
                    Stato impianto all'arrivo sul posto <span className="text-rose-500 font-bold">*</span>
                  </label>
                  <select
                    className="input font-semibold"
                    value={statoImpiantoArrivo}
                    onChange={(e) => setStatoImpiantoArrivo(e.target.value)}
                  >
                    {statiImpiantoOpzioni.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>

                  {(statoImpiantoArrivo.toLowerCase() === 'altro') && (
                    <input
                      className="input mt-2 font-semibold"
                      value={statoImpiantoAltro}
                      onChange={(e) => setStatoImpiantoAltro(e.target.value)}
                      placeholder="Specifica lo stato dell'impianto..."
                      required
                    />
                  )}
                </div>
              ) : (
                <div>
                  <label className="label">
                    Stato impianto all'arrivo sul posto <span className="text-rose-500 font-bold">*</span>
                  </label>
                  <input
                    className="input font-semibold"
                    value={statoImpiantoAltro}
                    onChange={(e) => {
                      setStatoImpiantoAltro(e.target.value);
                      setStatoImpiantoArrivo('Altro');
                    }}
                    placeholder="Descrivi lo stato dell'impianto..."
                    required
                  />
                </div>
              )}

              {c.impianto?.tipo === 'IS' ? (
                <div className="space-y-3">
                  <div>
                    <label className="label">
                      Causa principale del guasto <span className="text-rose-500 font-bold">*</span>
                    </label>
                    <input
                      type="text"
                      className="input font-semibold text-sm"
                      value={causaGuasto}
                      onChange={(e) => setCausaGuasto(e.target.value)}
                      placeholder="Seleziona una o più cause sotto o specifica..."
                      required
                    />
                    <p className="text-[11px] text-slate-400 font-medium pt-1">
                      È possibile selezionare più opzioni. Se inserisci più voci a mano, separale con un trattino " - "
                    </p>
                  </div>

                  <div className="space-y-2 pt-1">
                    <span className="text-xs text-slate-500 font-semibold block">
                      Cause frequenti (selezione multipla):
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {causeGuastoOpzioni
                        .filter((cg) => cg.toLowerCase() !== 'altro')
                        .map((cg) => {
                          const selected = isMultiOpzioneSelected(causaGuasto, cg);
                          return (
                            <button
                              key={cg}
                              type="button"
                              onClick={() => setCausaGuasto(toggleMultiOpzione(causaGuasto, cg))}
                              className={`px-3 py-2 rounded-xl border text-xs font-bold transition active:scale-95 cursor-pointer ${
                                selected
                                  ? 'border-emerald-600 bg-emerald-50 text-emerald-800 shadow-2xs'
                                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                              }`}
                            >
                              {cg}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="label">
                    Causa principale del guasto <span className="text-rose-500 font-bold">*</span>
                  </label>
                  <input
                    type="text"
                    className="input font-semibold text-sm py-3"
                    value={causaGuasto}
                    onChange={(e) => setCausaGuasto(e.target.value)}
                    placeholder="Descrivi la causa del guasto..."
                    required
                  />
                  <p className="text-[11px] text-slate-400 mt-1.5 font-medium">
                    È possibile selezionare più opzioni. Se inserisci più voci a mano, separale con un trattino " - "
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* PASSO 2: ORARI E AZIONI ESEGUITE */}
        {step === 2 && (
          <section className="card-section space-y-4 animate-fade-in p-5">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
              <div className="w-7 h-7 rounded-full bg-slate-900 text-white font-extrabold text-xs flex items-center justify-center">
                2
              </div>
              <div>
                <h3 className="section-title mb-0">Orari e Descrizione Intervento</h3>
                <p className="text-xs text-slate-500">Tempi di lavorazione ed azioni eseguite sul posto</p>
              </div>
            </div>

            {/* Riquadro informativo Data/Ora Creazione Chiamata */}
            {c?.dataOra && (
              <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 flex flex-wrap items-center justify-between gap-2 text-xs font-semibold">
                <div className="flex items-center gap-2 text-slate-700">
                  <Clock className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Data e Ora Creazione Chiamata:</span>
                </div>
                <span className="font-extrabold text-slate-900 bg-white px-2.5 py-1 rounded-xl border border-slate-200 shadow-2xs">
                  {new Date(c.dataOra).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label">
                  Inizio lavoro sul posto <span className="text-rose-500 font-bold">*</span>
                </label>
                <input
                  className={`input font-semibold ${errOrari ? 'border-rose-400 focus:border-rose-500 ring-2 ring-rose-200' : ''}`}
                  type="datetime-local"
                  value={inizioLavoroAt}
                  onChange={(e) => setInizioLavoroAt(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="label">
                  Fine lavoro sul posto <span className="text-rose-500 font-bold">*</span>
                </label>
                <input
                  className={`input font-semibold ${errOrari ? 'border-rose-400 focus:border-rose-500 ring-2 ring-rose-200' : ''}`}
                  type="datetime-local"
                  value={fineLavoroAt}
                  onChange={(e) => setFineLavoroAt(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Avviso in tempo reale se gli orari scelti non sono validi */}
            {errOrari && (
              <div className="p-3.5 rounded-2xl bg-rose-50 border border-rose-200 text-rose-900 flex items-start gap-2.5 text-xs animate-slide-up">
                <AlertTriangle className="w-4.5 h-4.5 text-rose-600 shrink-0 mt-0.5" />
                <div className="space-y-0.5">
                  <p className="font-extrabold text-rose-950">{errOrari.titolo}</p>
                  <p className="leading-relaxed">{errOrari.messaggio}</p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="label mb-0">
                  Descrizione dettagliata intervento svolto <span className="text-rose-500 font-bold">*</span>
                </label>
                {isAiEnabled && (
                  <div className="flex items-center gap-1.5">
                    {/* Pulsante Dettatura Vocale */}
                    <button
                      type="button"
                      onClick={handleToggleSpeech}
                      className={`px-2.5 py-1 rounded-xl text-xs font-bold border transition flex items-center gap-1.5 shadow-2xs ${
                        isListening
                          ? 'bg-rose-50 border-rose-300 text-rose-700 animate-pulse'
                          : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                      }`}
                      title={isListening ? 'Ferma dettatura' : 'Attiva dettatura vocale'}
                    >
                      {isListening ? (
                        <>
                          <MicOff className="w-3.5 h-3.5 text-rose-600" />
                          <span>Ascolto…</span>
                        </>
                      ) : (
                        <>
                          <Mic className="w-3.5 h-3.5 text-slate-500" />
                          <span>Dettatura</span>
                        </>
                      )}
                    </button>

                    {/* Pulsante Migliora con AI */}
                    <button
                      type="button"
                      onClick={handleMiglioraConAi}
                      disabled={isAiLoading || !azioniEseguite.trim()}
                      className="px-3 py-1 rounded-xl text-xs font-extrabold border border-emerald-500/30 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 disabled:opacity-50 disabled:pointer-events-none transition flex items-center gap-1.5 shadow-2xs"
                      title="Corregge refusi vocali e applica il glossario del capitolato"
                    >
                      {isAiLoading ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600" />
                          <span>Correzione…</span>
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
                          <span>Migliora con AI</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              <textarea
                className="input min-h-[140px] resize-y text-xs sm:text-sm font-medium"
                value={azioniEseguite}
                onChange={(e) => setAzioniEseguite(e.target.value)}
                placeholder="Descrivi in dettaglio l'intervento svolto (puoi anche usare la dettatura vocale al microfono)..."
                required
              />
            </div>
          </section>
        )}

        {/* PASSO 3: MATERIALI & RICAMBI */}
        {step === 3 && (
          <section className="card-section space-y-4 animate-fade-in p-5">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
              <div className="w-7 h-7 rounded-full bg-slate-900 text-white font-extrabold text-xs flex items-center justify-center">
                3
              </div>
              <div>
                <h3 className="section-title mb-0">Materiali & Ricambi Impiegati</h3>
                <p className="text-xs text-slate-500">Registra i ricambi utilizzati durante l'intervento</p>
              </div>
            </div>

            {/* Materiali */}
            <div className="space-y-3">
              <div className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                <Package className="w-4 h-4 text-emerald-600" />
                <span>Materiali e ricambi utilizzati (opzionale)</span>
              </div>

              <div className="space-y-2.5">
                {materiali.map((row, index) => (
                  <div key={index} className="rounded-2xl border border-slate-200 bg-slate-50/50 p-3">
                    <div className="flex flex-wrap sm:flex-nowrap items-end gap-2">
                      {/* Descrizione ricambio - prende tutto lo spazio orizzontale rimanente */}
                      <div className="flex-1 min-w-[180px]">
                        <label className="label text-xs font-bold text-slate-700">Descrizione ricambio</label>
                        <input
                          className="input text-xs sm:text-sm py-2 font-semibold w-full"
                          value={row.descrizione}
                          onChange={(e) => aggiornaMateriale(index, 'descrizione', e.target.value)}
                          placeholder="Es. lampada LED, cavo quadripolare..."
                        />
                      </div>

                      {/* U.M. (a sinistra della Quantità) */}
                      <div className="w-20 shrink-0">
                        <label className="label text-xs font-bold text-slate-700">U.M.</label>
                        <select
                          className="input text-xs sm:text-sm py-2 font-extrabold bg-white text-slate-800 border-slate-300 w-full text-center"
                          value={row.um || 'N.'}
                          onChange={(e) => aggiornaMateriale(index, 'um', e.target.value as 'N.' | 'MT.')}
                        >
                          <option value="N.">N.</option>
                          <option value="MT.">MT.</option>
                        </select>
                      </div>

                      {/* Quantità (solo numeri, a destra dell'U.M.) */}
                      <div className="w-20 shrink-0">
                        <label className="label text-xs font-bold text-slate-700">Quantità</label>
                        <input
                          className="input text-xs sm:text-sm py-2 font-bold text-center w-full"
                          type="number"
                          inputMode="numeric"
                          min="0"
                          step="any"
                          value={row.quantita}
                          onChange={(e) => aggiornaMateriale(index, 'quantita', e.target.value)}
                          placeholder="0"
                        />
                      </div>

                      {/* Pulsante X presente se la riga contiene dati o se ci sono più righe */}
                      {(row.descrizione.trim() !== '' || (row.quantita && row.quantita.trim() !== '0') || materiali.length > 1) && (
                        <div className="shrink-0 h-[38px] flex items-center pb-0.5">
                          <button
                            type="button"
                            className="w-8 h-8 rounded-xl border border-slate-200 bg-white text-slate-400 hover:text-rose-600 hover:bg-rose-50 hover:border-rose-200 flex items-center justify-center transition shadow-2xs"
                            onClick={() => rimuoviMateriale(index)}
                            title="Elimina riga materiale"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="btn-outline w-full py-2.5 text-xs font-bold gap-1.5 border-dashed border-slate-300 hover:border-emerald-500 hover:bg-emerald-50/50 hover:text-emerald-800"
                  onClick={() => setMateriali((prev) => [...prev, { descrizione: '', quantita: '0', um: 'N.' }])}
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Aggiungi riga materiale</span>
                </button>
              </div>
            </div>
          </section>
        )}

        {/* PASSO 4: ESITO TECNICO DELL'INTERVENTO */}
        {step === 4 && (
          <section className="card-section space-y-4 animate-fade-in p-5">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
              <div className="w-7 h-7 rounded-full bg-slate-900 text-white font-extrabold text-xs flex items-center justify-center">
                4
              </div>
              <div>
                <h3 className="section-title mb-0">Esito Tecnico dell'Intervento</h3>
                <p className="text-xs text-slate-500">
                  {c.impianto?.tipo === 'IS'
                    ? 'Seleziona o indica il risultato tecnico delle lavorazioni svolte'
                    : 'Descrivi il risultato tecnico delle lavorazioni svolte'}
                </p>
              </div>
            </div>

            <div className="space-y-3.5">
              {c.impianto?.tipo === 'IS' ? (
                <div className="space-y-3">
                  <div>
                    <label className="label">
                      Esito tecnico dell'intervento <span className="text-rose-500 font-bold">*</span>
                    </label>
                    <input
                      type="text"
                      className="input font-semibold text-sm"
                      value={esito}
                      onChange={(e) => setEsito(e.target.value)}
                      placeholder="Seleziona una scelta rapida sotto o specifica..."
                      required
                    />
                  </div>

                  <div className="space-y-2 pt-1">
                    <span className="text-xs text-slate-500 font-semibold block">
                      Scelte rapide frequenti:
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {esitiOpzioni.map((es) => (
                        <button
                          key={es}
                          type="button"
                          onClick={() => setEsito(es)}
                          className={`px-3 py-2 rounded-xl border text-xs font-bold transition active:scale-95 cursor-pointer ${
                            esito === es
                              ? 'border-emerald-600 bg-emerald-50 text-emerald-800 shadow-2xs'
                              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          {es}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="label">
                    Esito tecnico dell'intervento <span className="text-rose-500 font-bold">*</span>
                  </label>
                  <input
                    type="text"
                    className="input font-semibold text-sm py-3"
                    value={esito}
                    onChange={(e) => setEsito(e.target.value)}
                    placeholder="Descrivi l'esito tecnico dell'intervento..."
                    required
                    autoFocus
                  />
                  <p className="text-[11px] text-slate-400 mt-1.5 font-medium">
                    Impianto tipologia <strong>{c.impianto?.tipo || 'Non-IS'}</strong>: inserisci l'esito tecnico in formato testo libero.
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* PASSO 5: STATO FINALE CHIAMATA & FOTO */}
        {step === 5 && (
          <section className="card-section space-y-4 animate-fade-in p-5">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
              <div className="w-7 h-7 rounded-full bg-slate-900 text-white font-extrabold text-xs flex items-center justify-center">
                5
              </div>
              <div>
                <h3 className="section-title mb-0">Foto Intervento & Stato Finale</h3>
                <p className="text-xs text-slate-500">Allega la documentazione fotografica e definisci la destinazione della chiamata</p>
              </div>
            </div>

            {/* Foto dell'intervento */}
            <div className="space-y-3">
              <div className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                <Camera className="w-4 h-4 text-emerald-600" />
                <span>Foto dell'intervento (opzionale)</span>
              </div>

              {fotoFiles.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {fotoFiles.map((file, index) => (
                    <div key={index} className="relative group rounded-xl border border-slate-200 bg-white overflow-hidden aspect-square">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Foto ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        className="absolute top-1 right-1 z-10 rounded-full bg-rose-600 text-white p-1 opacity-100 hover:bg-rose-700 shadow-lg"
                        onClick={() => rimuoviFoto(index)}
                        title="Rimuovi foto"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Selettori foto: Fotocamera e Galleria */}
              <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/60 p-4 text-center space-y-3">
                <div className="flex items-center justify-center gap-3 text-slate-400">
                  <Camera className="w-6 h-6 text-slate-400" />
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">oppure</span>
                  <ImageIcon className="w-6 h-6 text-slate-400" />
                </div>

                {/* Input Fotocamera */}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  id="foto-upload-camera"
                  className="hidden"
                  onChange={(e) => {
                    aggiungiFoto(Array.from(e.target.files || []));
                    e.target.value = '';
                  }}
                  disabled={compressioneFoto || fotoFiles.length >= 10}
                />

                {/* Input Galleria */}
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  id="foto-upload-gallery"
                  className="hidden"
                  onChange={(e) => {
                    aggiungiFoto(Array.from(e.target.files || []));
                    e.target.value = '';
                  }}
                  disabled={compressioneFoto || fotoFiles.length >= 10}
                />

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2">
                  <label
                    htmlFor="foto-upload-camera"
                    className={`btn-outline inline-flex cursor-pointer text-xs font-bold gap-1.5 bg-white justify-center py-2.5 px-3.5 shadow-2xs ${
                      fotoFiles.length >= 10 || compressioneFoto ? 'opacity-50 pointer-events-none' : ''
                    }`}
                  >
                    <Camera className="w-4 h-4 text-emerald-600" />
                    <span>Scatta con fotocamera</span>
                  </label>

                  <label
                    htmlFor="foto-upload-gallery"
                    className={`btn-outline inline-flex cursor-pointer text-xs font-bold gap-1.5 bg-white justify-center py-2.5 px-3.5 shadow-2xs ${
                      fotoFiles.length >= 10 || compressioneFoto ? 'opacity-50 pointer-events-none' : ''
                    }`}
                  >
                    <ImageIcon className="w-4 h-4 text-sky-600" />
                    <span>Scegli da galleria</span>
                  </label>
                </div>

                <p className="text-[11px] text-slate-400">
                  {compressioneFoto
                    ? 'Ottimizzazione immagini in corso…'
                    : fotoFiles.length >= 10
                    ? 'Limite massimo di 10 foto raggiunto'
                    : fotoFiles.length > 0
                    ? `${fotoFiles.length}/10 foto allegate · Puoi aggiungerne altre`
                    : 'Puoi scattare foto sul posto o caricarle dalla galleria del telefono'}
                </p>
              </div>
            </div>

            {/* Stato finale della chiamata */}
            <div className="pt-3 border-t border-slate-100 space-y-3">
              <label className="label">
                Stato finale della chiamata <span className="text-rose-500 font-bold">*</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
                <label
                  className={`flex items-start gap-3 p-4 rounded-2xl border-2 transition cursor-pointer ${
                    statoDestinazione === 'chiusa'
                      ? 'border-emerald-500 bg-emerald-50/50 shadow-xs'
                      : 'border-slate-200 bg-white hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="dest"
                    className="mt-1 accent-emerald-600 w-4 h-4"
                    checked={statoDestinazione === 'chiusa'}
                    onChange={() => setStatoDestinazione('chiusa')}
                  />
                  <div>
                    <div className="font-extrabold text-sm text-slate-900">Chiusa definitivamente</div>
                    <div className="text-xs text-slate-500 mt-0.5">Intervento completato con successo.</div>
                  </div>
                </label>

                <label
                  className={`flex items-start gap-3 p-4 rounded-2xl border-2 transition cursor-pointer ${
                    statoDestinazione === 'sospesa'
                      ? 'border-amber-500 bg-amber-50/50 shadow-xs'
                      : 'border-slate-200 bg-white hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="dest"
                    className="mt-1 accent-amber-600 w-4 h-4"
                    checked={statoDestinazione === 'sospesa'}
                    onChange={() => setStatoDestinazione('sospesa')}
                  />
                  <div>
                    <div className="font-extrabold text-sm text-slate-900">Da Terminare (Torna in Aperte)</div>
                    <div className="text-xs text-slate-500 mt-0.5">Richiede un successivo intervento: torna tra le chiamate Aperte per essere ripresa in carico.</div>
                  </div>
                </label>
              </div>
            </div>
          </section>
        )}
      </div>
  );
}
